import { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { FormattedMessage, useIntl } from 'gatsby-plugin-react-intl';
import sanitizeHtml from 'sanitize-html';
import uuid from 'react-uuid';
import lunr from 'lunr';
import { graphql, useStaticQuery } from 'gatsby';

// eslint-disable-next-line import/no-relative-packages
import { enhanceLunr } from '../../../../plugins/gatsby-plugin-local-lunr/common';
import { useLunr } from '../../../hooks';
import Link from '../../Core/Link';
import Container from '../../Core/Container';
import Loader from '../../Core/Loader';
import { LocationContext } from '../../Providers/Geolocation';
import { SearchContext } from '../../Providers/SearchIndex';

import {
  StyledGlobalSearch,
  StyledContainer,
  SearchTitle,
  SearchFilterWrapper,
  SearchButton,
  Wrapper,
  NoResultsWrapper,
  NoResults,
  Results,
  Result,
  ResultTitle,
  ResultDescription,
  ResultCategory,
  StyledPagination,
} from './styles';

const resultsPerPage = 10;

const filters = [
  { filter: 'all', label: 'AllResults', value: '' },
  { filter: 'products', label: 'Products', value: 'ProductCategory' },
  { filter: 'blogs', label: 'Blog', value: 'BlogCategory' },
  { filter: 'faqs', label: 'FAQs', value: 'FAQsCategory' },
  {
    filter: 'videos',
    label: 'VideoTutorialsCategory',
    value: 'VideoTutorialsCategory',
  },
];

const SearchFilters = ({
  onClick = () => {},
  activeFilter = 'all',
  french = false,
}) => {
  return (
    <SearchFilterWrapper className="no-bullets" role="tablist">
      {filters.map(({ label, value, filter }) => {
        if (french && value === 'BlogCategory') return null;
        return (
          <SearchButton
            key={uuid()}
            type="button"
            onClick={() => onClick(filter)}
            active={activeFilter === filter}
            role="tab"
          >
            <FormattedMessage id={label} />
          </SearchButton>
        );
      })}
    </SearchFilterWrapper>
  );
};

const SearchResults = ({ location }) => {
  const locationContext = useContext(LocationContext);
  const { lunrjs, updateLunrjs } = useContext(SearchContext);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(``);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const [french, setFrench] = useState(false);
  const intl = useIntl();
  const [index, setIndex] = useState({});
  const [store, setStore] = useState({});

  const {
    localSearch: { publicLunrUrl },
  } = useStaticQuery(graphql`
    query HeaderSearchQuery {
      localSearch {
        publicLunrUrl
      }
    }
  `);

  useEffect(() => {
    if (lunrjs[intl.locale].index) {
      setIndex(JSON.parse(lunrjs[intl.locale].index));
    }
    if (lunrjs[intl.locale].store) {
      setStore(lunrjs[intl.locale].store);
    }
  }, [lunrjs]);

  if (!lunr.fr) {
    enhanceLunr(lunr, ['fr']);
  }

  const updateFilter = newFilter => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isFrench = intl.locale === 'fr' || locationContext === 'Quebec';
      setFrench(isFrench);
    }
  }, []);

  useEffect(() => {
    const lunrUrl = intl.locale === 'en' ? publicLunrUrl.en : publicLunrUrl.fr;
    if (!lunrjs[intl.locale].index && !lunrjs[intl.locale].store) {
      fetch(lunrUrl)
        .then(idx => idx.json())
        .then(idx => {
          const updatedLunrObj = { ...lunrjs[intl.locale], ...idx };
          const updatedLunrLocale = {
            ...lunrjs,
            ...{ [intl.locale]: updatedLunrObj },
          };
          updateLunrjs(updatedLunrLocale);
        });
    }
  }, [intl.locale, lunrjs]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const params = new URL(document.location).searchParams;
      const searchQuery = params.get('searchQuery');
      const searchFilter = params.get('filter');
      if (searchFilter) updateFilter(searchFilter);
      setQuery(searchQuery);
      if (lunrjs[intl.locale].index) setLoading(false);
    }
  }, [location, index, lunrjs]);

  const results = useLunr(query, index, store);
  const filterResults = (res, filterQuery) => {
    if (!filterQuery) {
      return res;
    }
    return res.filter(s => {
      return s.category.includes(filterQuery);
    });
  };

  const filterResult = useMemo(() => {
    const selectedFilter = filters.find(filt => filt.filter === filter);
    return filterResults(results, selectedFilter.value);
  }, [query, filter, lunrjs, index]);

  useEffect(() => {
    if (!filterResult) {
      setLoading(false);
    }
  }, [filterResult]);

  return (
    <>
      <Container type="auto-margin">
        <StyledGlobalSearch
          type="is-open"
          placeholder={`${intl.formatMessage({
            id: 'GlobalSearchPlaceholder',
          })}`}
          predictiveSearch
          predictiveSearchClass="centered-results"
        />
      </Container>
      <StyledContainer>
        <Wrapper>
          <SearchTitle>
            <FormattedMessage id="SearchResults" />{' '}
            <span>{sanitizeHtml(query)}</span>
          </SearchTitle>
          <SearchFilters
            onClick={updateFilter}
            activeFilter={filter}
            french={french}
          />
          {filterResult.length >= 1 && !loading && (
            <Results className="no-bullets">
              {filterResult
                .map(({ id, pageSlug, title, description, category }) => {
                  return (
                    <Result key={id}>
                      <Link
                        className="unstyled-link"
                        id={
                          category === 'FAQsCategory'
                            ? 'faq-question-page'
                            : category
                        }
                        to={pageSlug}
                      >
                        {title && (
                          <ResultTitle>{ReactHtmlParser(title)}</ResultTitle>
                        )}
                        {description && (
                          <ResultDescription>
                            {ReactHtmlParser(description)}
                          </ResultDescription>
                        )}
                        <ResultCategory>
                          <FormattedMessage id={category} />
                        </ResultCategory>
                      </Link>
                    </Result>
                  );
                })
                .filter(
                  (_, ind) =>
                    ind >= currentPage * resultsPerPage - resultsPerPage &&
                    ind < currentPage * resultsPerPage,
                )}
            </Results>
          )}
          {!filterResult.length && !loading && (
            <NoResultsWrapper>
              <NoResults>
                <FormattedMessage id="NoResults" />
              </NoResults>
            </NoResultsWrapper>
          )}
          {loading && <Loader />}
          {filterResult.length > resultsPerPage && (
            <StyledPagination
              totalArticle={filterResult.length}
              articlesPerPage={resultsPerPage}
              currentPage={currentPage}
              paginate={setCurrentPage}
            />
          )}
        </Wrapper>
      </StyledContainer>
    </>
  );
};

SearchResults.propTypes = {
  location: PropTypes.string.isRequired,
};

SearchFilters.propTypes = {
  onClick: PropTypes.func,
  activeFilter: PropTypes.string,
  french: PropTypes.bool,
};

export default SearchResults;
